import clsx from "clsx";
import { HTMLAttributes } from "react";
import { Icon, IconProps } from "../icon/Icon";
import * as styles from "./Note.css";

/** Visual intent of the note. */
export type NoteVariant = "info" | "warning" | "error";

/** Size of the note layout. */
export type NoteSize = "default" | "compact";

const DEFAULT_ICONS: Record<NoteVariant, IconProps["name"]> = {
  info: "info",
  warning: "warning",
  error: "warning"
};

export interface NoteProps extends HTMLAttributes<HTMLDivElement> {
  /** Icon name. Defaults based on variant (info→info, warning/error→warning). */
  icon?: IconProps["name"];
  /** Text content of the note. */
  text: string;
  /** Visual intent — controls background/text color. Defaults to "info". */
  variant?: NoteVariant;
  /** Layout size. "compact" for inline warnings, "default" for prominent blocks. */
  size?: NoteSize;
}

export function Note({
  icon,
  text,
  variant = "info",
  size = "default",
  className,
  ...props
}: NoteProps) {
  const resolvedIcon = icon ?? DEFAULT_ICONS[variant];

  return (
    <div
      className={clsx(
        styles.noteBase,
        styles.noteSizes[size],
        styles.noteVariants[variant],
        className
      )}
      {...props}
    >
      <span
        className={clsx(styles.noteIcon, styles.noteIconSizes[size])}
        aria-hidden="true"
      >
        <Icon name={resolvedIcon} />
      </span>
      <span className={clsx(styles.noteText, styles.noteTextSizes[size])}>
        {text}
      </span>
    </div>
  );
}
